// Single component renders an alert for any of the JGZ advice trees
// (weight, length, head circumference). Only shown when the result is an
// alert code — silent on OK and silent on "need more data" codes (no
// news is good news in clinical UX).
import { growth } from '@shared/index.ts';

type Tone = 'danger' | 'warn';

type Props = {
  /** "Weight" / "Length" / "Head circumference" — drives the title. */
  metric: string;
  tone: Tone;
  /** Pre-computed advice result. Null → render nothing. */
  result: growth.AdviceResult | null;
};

export function AdviceAlertCard({ metric, tone, result }: Props) {
  if (!result) return null;

  const accent = tone === 'danger' ? 'var(--danger)' : 'var(--warn)';
  const bg =
    tone === 'danger'
      ? 'color-mix(in oklab, var(--danger) 10%, var(--bg-card))'
      : 'color-mix(in oklab, var(--warn) 10%, var(--bg-card))';
  const border = `1px solid color-mix(in oklab, ${accent} 28%, transparent)`;

  return (
    <div
      style={{
        padding: '14px 16px',
        borderRadius: 16,
        background: bg,
        border,
        display: 'flex',
        gap: 12,
        alignItems: 'baseline',
      }}
    >
      <div style={{ flex: 1 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: accent,
            textTransform: 'uppercase',
            letterSpacing: 0.04,
          }}
        >
          {metric} alert · NL5
        </div>
        <div style={{ fontSize: 14, color: 'var(--text-1)', marginTop: 4, lineHeight: 1.35 }}>
          {result.message}
        </div>
        {result.sdsNow != null && (
          <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 6 }}>
            SDS {result.sdsNow >= 0 ? '+' : ''}
            {result.sdsNow.toFixed(2)} (P{result.percentile?.toFixed(0)}) · JGZ code {result.msgcode}
          </div>
        )}
      </div>
    </div>
  );
}
